import { useRef, useState } from 'react';
import type { TEntityChoice } from '@verifime/components';
import type { TConfirmAction, TEntityPlan } from '../entityPlan';
import { reportBusinessRefusal } from '../../reportBusinessRefusal';
import { useConfirmEntityChoice } from './useConfirmEntityChoice';
import { useShareIdentities } from './useShareIdentities';

/** Why person-api refused the confirmation or the grant, when it did so for a reason the customer is told. */
export type TConfirmRefusal = 'duplicate-identity' | 'held-document-type-not-accepted';

type TConfirmActionOptions = {
  trackingReference: string | undefined;
  /** The client the plan is for: who the share is granted to. Empty until the plan has loaded. */
  organisationId: string;
  onConfirmed: (action: TConfirmAction) => void;
  /** A 409 that is not a refusal the customer is told, with the `errorCode` it carried, if any. */
  onConflict: (errorCode?: string) => void;
  /** Called once, just before the first grant of an action is sent. */
  onShareStarted?: () => void;
  /**
   * Called when an action ends without handing on (a failed or refused share, a 409, a failure
   * of the confirmation, or a hand-on that threw): the screen is not being left. Never called
   * after an action that handed on.
   */
  onShareEnded?: () => void;
};

/**
 * The confirm screen's primary action: confirm the choice (unless an open request already holds it),
 * then share what the plan shares with the client, then hand the plan's action on. `hasError` is
 * true after a failure of the confirmation or the share. The share comes after the confirmation:
 * the grant closes the customer's requests, which must exist first.
 *
 * Each refusal shown to the customer is also reported to monitoring as a business refusal, once for
 * each refusal person-api answers with (see `setRefusal`).
 *
 * `refusal` is set when person-api refuses the confirmation as a duplicate identity (R15, 409 with
 * its `errorCode`), or refuses the confirmation or the grant because the customer holds a photo card
 * or ImmiCard that the client has not opted into (409 with its own `errorCode`). It is not an error
 * and it does not route: `onConflict` is not called, so there is no Legacy save, no redirect and no
 * joined-invite boundary. The view tells the customer. It clears when the next run starts.
 */
export function useConfirmAction({
  trackingReference,
  organisationId,
  onConfirmed,
  onConflict,
  onShareStarted,
  onShareEnded,
}: TConfirmActionOptions) {
  const { confirm, hasError: hasConfirmError } = useConfirmEntityChoice(trackingReference ?? '');
  const { share } = useShareIdentities(organisationId);
  const [cannotConfirm, setCannotConfirm] = useState(false);
  const [hasShareError, setHasShareError] = useState(false);
  const [refusal, setRefusalState] = useState<TConfirmRefusal | undefined>(undefined);
  // The one way a refusal is shown to the customer, so a refusal that is shown is a refusal that is
  // reported: once for each refusal person-api answers with, a retry that is refused again included.
  // The parameter type ties the two lists: a `TConfirmRefusal` that is not in
  // `BUSINESS_REFUSAL_KINDS` does not compile.
  const setRefusal = (kind: TConfirmRefusal) => {
    setRefusalState(kind);
    reportBusinessRefusal('confirm-entity', kind);
  };
  // Synchronous guard: state updates land too late to stop a double click sending a second share.
  // It holds while an action runs and after one has finished: a finished action is not run again
  // (the caller is leaving the screen). A failed or refused one releases it, so the customer can
  // try again.
  const isRunningRef = useRef(false);
  // The choice this view already confirmed with the server (a 204). Shares run one after another
  // and a grant closes the open requests, so after a partial share the requests no longer show the
  // choice and the plan reads as unconfirmed: the retry must share what is left, not confirm again.
  const confirmedRef = useRef<{ trackingReference?: string; choice: TEntityChoice }>();

  const isConfirmedHere = (plan: TEntityPlan): boolean =>
    confirmedRef.current?.trackingReference === trackingReference &&
    confirmedRef.current?.choice === plan.choice;

  /**
   * Confirms the choice, unless an open request already holds it or this view already confirmed it.
   * True when the action may go on.
   */
  const confirmChoice = async (plan: TEntityPlan): Promise<boolean> => {
    if (plan.isConfirmed || isConfirmedHere(plan)) {
      return true;
    }
    if (!trackingReference) {
      console.error('Confirm entity view: no tracking reference to confirm the choice with');
      setCannotConfirm(true);
      return false;
    }
    const result = await confirm(plan.choice);
    if (result.outcome === 'conflict') {
      onConflict(result.errorCode);
    }
    if (
      result.outcome === 'duplicate-identity' ||
      result.outcome === 'held-document-type-not-accepted'
    ) {
      setRefusal(result.outcome);
    }
    if (result.outcome === 'confirmed') {
      confirmedRef.current = { trackingReference, choice: plan.choice };
    }
    return result.outcome === 'confirmed';
  };

  /**
   * Shares what the action shares. True when nothing is left unshared. A failed share is an error
   * only when what it tried is still not shared: when it is now shared (it was shared in the
   * meantime), the plan shows it as already shared and there is nothing to report (#665). Either
   * way the action does not hand on: the customer reads the refreshed screen and goes on from it.
   */
  const shareIdentities = async ({ shareCustomerIds }: TConfirmAction): Promise<boolean> => {
    if (shareCustomerIds.length === 0) {
      return true;
    }
    onShareStarted?.();
    const result = await share(shareCustomerIds);
    const refusal = result.outcome === 'failed' && !result.nowShared ? result.refusal : undefined;
    if (refusal) {
      setRefusal(refusal);
    }
    setHasShareError(result.outcome === 'failed' && !result.nowShared && !refusal);
    return result.outcome === 'shared';
  };

  const run = async (plan: TEntityPlan) => {
    if (isRunningRef.current) {
      return;
    }
    isRunningRef.current = true;
    setHasShareError(false);
    setRefusalState(undefined);
    let isDone = false;
    try {
      if ((await confirmChoice(plan)) && (await shareIdentities(plan.action))) {
        onConfirmed(plan.action);
        isDone = true;
      }
    } catch {
      // The hand-on threw (a failed navigation): the customer is still here, and may try again.
      // Reason only: the error can carry ids.
      console.error('Confirm entity view: handing the action on failed');
    } finally {
      isRunningRef.current = isDone;
      if (!isDone) {
        onShareEnded?.();
      }
    }
  };

  return { run, hasError: hasConfirmError || cannotConfirm || hasShareError, refusal };
}
